// skymavisNFTService.ts

import {  ContractBase, TokenDataBase, TokenDataType } from "../commonInterfacesAndEnums";


import { fetchSkyMavisNFTsBackend } from "../../backendHelpers";
interface SkyMavisNFTResponse {
    data: {
        nfts: {
            token: TokenDataBase;
            chain: string;
            contractAddress: string;
            quantity: string;
            tokenId: string;
            [key: string]: any;
        }[];
        nextCursor: string;
    };
    success: boolean;
    message: string;
}

/**
 * Fetches and processes NFTs from Sky Mavis (Ronin) blockchain
 * @param {string} [walletAddress] Optional wallet address to fetch NFTs for
 * @returns {Promise<any[]>} Array of processed NFTs or empty array if request fails
 */
export async function fetchSkyMavisNFTs(walletAddress?: string): Promise<any[]> {
    try {
        const response = await fetchSkyMavisNFTsBackend(walletAddress);
        
        if (!response || !response.data || !response.data.nfts) {
            return [];
        }

        return processSkyMavisNFTs(response.data.nfts);
    } catch (error) {
        console.error('Error in fetchSkyMavisNFTs:', error);
        return [];
    }
}

/**
 * Processes raw NFT data from Sky Mavis into standardized format
 * @param {any[]} nfts Raw NFT data from Sky Mavis API
 * @returns {any[]} Processed NFT array
 */
function processSkyMavisNFTs(nfts: any[]): any[] {
    return nfts.map(nft => {
        const processedNFT = {
            token: {
                type: nft.token.type as TokenDataType,
                creatorId: nft.token.creatorId,
                name: nft.token.name,
                description: nft.token.description,
                externalUri: nft.token.externalUri,
                imageUri: nft.token.imageUri,
                videoUri: nft.token.videoUri,
                supply: nft.token.supply,
                decimals: nft.token.decimals,
                attributes: nft.token.attributes || [],
                externalMetadataUri: nft.token.externalMetadataUri,
                assetContractId: nft.token.assetContractId,
                tokenId: nft.token.tokenId,
                lazyForm: null,
                encodedFormBytes: null
            },
            imageUri: nft.imageUri,
            chain: "ronin",
            name: nft.name,
            symbol: nft.symbol || "",
            type: nft.type,
            confirmed: true,
            contractAddress: nft.contractAddress,
            creatorId: nft.creatorId,
            isExternal: true,
            isGlobal: false,
            quantity: nft.quantity,
            exchange: [],
            tokenId: nft.tokenId
        };

        // Dispatch event for each NFT
        window.dispatchEvent(new CustomEvent('tokenAdded', {
            detail: {
                'nft': processedNFT
            }
        }));

        return processedNFT;
    });
}

/**
 * Gets contract data for Sky Mavis NFTs
 * @param {any[]} nfts Array of NFTs to extract contract data from
 * @returns {ContractBase[]} Array of contract data
 */
export function getSkyMavisContractData(nfts: any[]): ContractBase[] {
    const uniqueContracts = new Map<string, ContractBase>();
    
    nfts.forEach(nft => {
        if (!uniqueContracts.has(nft.contractAddress)) {
            uniqueContracts.set(nft.contractAddress, {
                _id: nft.token.assetContractId,
                id: nft.token.assetContractId,
                chain: "ronin" as any,
                name: nft.name,
                symbol: nft.symbol || "",
                type: nft.type,
                confirmed: true,
                contractAddress: nft.contractAddress,
                creatorId: nft.creatorId,
                isExternal: true,
                isGlobal: false
            } as ContractBase);
        }
    });
    
    return Array.from(uniqueContracts.values());
}

/**
 * Combines NFTs and their metadata into a single response
 * @param {any[]} nfts Array of NFTs
 * @returns {Object} Combined NFT data with metadata
 */
export function buildSkyMavisNFTResponse(nfts: any[]) {
    const contracts = getSkyMavisContractData(nfts);
    
    return {
        nfts: {
            nfts: nfts,
            contracts: contracts
        },
        success: true,
        message: "NFTs found"
    };
}